import React, { useEffect, useState } from "react";
import {
  VStack,
  Button,
  Box,
  Text,
  Divider,
  Icon,
  useColorModeValue,
  Tooltip,
  IconButton,
  Flex,
  Collapse,
  SimpleGrid,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useUser } from "../context/UserContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BsGrid,
  BsBoxSeam,
  BsListCheck,
  BsCreditCard,
  BsShieldCheck,
  BsClockHistory,
  BsStickies,
  BsPeople,
  BsBarChart,
  BsGear,
  BsPostage,
  BsShop,
  BsArrowLeft,
  BsChevronLeft,
  BsChevronRight,
  BsChevronDown,
  BsChevronUp,
} from "react-icons/bs";
import { FaWarehouse } from "react-icons/fa";

const navItems = [
  { label: "Dashboard", to: "/", icon: BsGrid },
  { label: "Categories", to: "/categories", icon: BsStickies },
  { label: "POS", to: "/pos", icon: BsBoxSeam },
  { label: "Excel", to: "/Excel", icon: BsPostage },
  { label: "Transactions", to: "/transactions", icon: BsShieldCheck },
  { label: "Orders", to: "/ComingSoon", icon: BsListCheck },
  { label: "Payments", to: "/ComingSoon", icon: BsCreditCard },
  { label: "Reconciliation", to: "/ComingSoon", icon: BsClockHistory },
  { label: "Inventory", to: "/ComingSoon", icon: BsBoxSeam },
  { label: "Materials", to: "/materials", icon: BsBoxSeam },
  { label: "Menu", to: "/ComingSoon", icon: BsStickies },
  { label: "Staff", to: "/staff", icon: BsPeople },
  { label: "Reports", to: "/ComingSoon", icon: BsBarChart },
  { label: "Settings", to: "/ComingSoon", icon: BsGear },
];

const marketplaceItems = [
  {
    label: "Marketplace Dashboard",
    to: "/novatra/marketplace",
  },
  {
    label: "Browse Materials",
    to: "/novatra/marketplace/browse",
  },
  {
    label: "My Listings",
    to: "/novatra/marketplace/my-listings",
    permission: "listing",
  },
  {
    label: "Create Listing",
    to: "/novatra/marketplace/listings/new",
    permission: "listing",
  },
  {
    label: "Import Listings",
    to: "/novatra/marketplace/listings/import",
    permission: "listing",
  },
  {
    label: "Reviews",
    to: "/novatra/marketplace/reviews",
    permission: "review",
  },
  {
    label: "Review Workflows",
    to: "/novatra/marketplace/workflows",
    permission: "review",
  },
  {
    label: "Negotiations",
    to: "/novatra/marketplace/negotiations",
    permission: "negotiation",
  },
  {
    label: "Approvals",
    to: "/novatra/marketplace/approvals",
    permission: "approval",
  },
  {
    label: "Purchase Orders",
    to: "/novatra/marketplace/purchase-orders",
    permission: "purchase",
  },
  {
    label: "Material Receipts",
    to: "/novatra/marketplace/receipts",
    permission: "receipt",
  },
  {
    label: "Stock Posting",
    to: "/novatra/marketplace/stock-posting",
    permission: "stock",
  },
  {
    label: "Completed",
    to: "/novatra/marketplace/completed",
  },
];

const hasModuleAccess = () => true;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { tenant, loggedInUser } = useUser();

  /*
   * Mobile breakpoint
   *
   * base -> mobile
   * md+  -> desktop
   */
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    {
      fallback: "md",
    },
  );

  /*
   * Mobile navigation is displayed as a full-page launcher
   * only on the root route.
   */
  const isHomePath = location.pathname === "/" || location.pathname === "";

  const [sidebarClosed, setSidebarClosed] = useState(false);

  const shouldShowMobileSidebar = isMobile && isHomePath && !sidebarClosed;

  /*
   * Reset mobile closed state when navigating away from home.
   */
  useEffect(() => {
    if (!isHomePath) {
      setSidebarClosed(false);
    }
  }, [isHomePath]);

  /*
   * User role
   */
  const [userRole, setUserRole] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const companyData = JSON.parse(localStorage.getItem("company") || "{}");

      return companyData.role || null;
    } catch (err) {
      return null;
    }
  });

  /*
   * Theme
   */
  const bg = useColorModeValue("gray.50", "gray.900");
  const borderClr = useColorModeValue("gray.200", "gray.700");

  const activeBg = useColorModeValue("teal.100", "teal.600");

  const activeColor = useColorModeValue("teal.700", "white");

  const textColor = useColorModeValue("gray.700", "gray.300");

  const hoverBg = useColorModeValue("gray.200", "gray.700");

  const footerColor = useColorModeValue("gray.500", "gray.500");

  const mobileTileBg = useColorModeValue("gray.100", "gray.700");

  /*
   * Route active check
   */
  const isRouteActive = (to) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  /*
   * Base navigation
   */
  const items = [...navItems];

  const shouldInsertRentals = true;
  items.splice(3, 0, {
    label: "Rentals", to: "/rentals", icon: FaWarehouse,
    children: [
      { label: "Assets", to: "/rentals/assets" },
      { label: "Reservations", to: "/rentals/reservations" },
      { label: "Bookings", to: "/rentals/bookings" },
    ],
  });

  const marketplaceEnabled = true;
  items.splice(4, 0, {
    label: "Marketplace", to: "/novatra/marketplace", icon: BsShop,
    children: marketplaceItems,
  });


  /*
   * Desktop collapsed sidebar state
   *
   * Important:
   * Mobile layout does NOT modify this state.
   */
  const STORAGE_KEY = "sidebar:collapsed";

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);

      return raw ? JSON.parse(raw) === true : false;
    } catch {
      return false;
    }
  });

  /*
   * Persist desktop collapsed state
   */
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
    } catch {}
  }, [collapsed]);

  /*
   * Listen for changes from other tabs
   */
  useEffect(() => {
    const onStorage = (event) => {
      try {
        if (event.key === STORAGE_KEY && event.newValue != null) {
          const next = JSON.parse(event.newValue);

          if (typeof next === "boolean" && next !== collapsed) {
            setCollapsed(next);
          }
        }

        if (event.key === "company" && event.newValue != null) {
          const companyData = JSON.parse(event.newValue);

          const newRole = companyData.role || null;

          setUserRole(newRole);
        }
      } catch (err) {
        // ignore invalid storage values
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("storage", onStorage);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", onStorage);
      }
    };
  }, [collapsed]);

  /*
   * Desktop collapse toggle
   */
  const toggleCollapsed = () => {
    const next = !collapsed;

    setCollapsed(next);

    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

        window.dispatchEvent(new Event("sidebar-toggle"));
      }
    } catch {}
  };

  /*
   * Expanded desktop parent menus
   */
  const [expanded, setExpanded] = useState(() => {
    const map = {};

    items.forEach((item) => {
      if (item.children && Array.isArray(item.children)) {
        const shouldOpen = item.children.some(
          (child) =>
            location.pathname === child.to ||
            location.pathname.startsWith(`${child.to}/`),
        );

        map[item.to] = shouldOpen;
      }
    });

    return map;
  });

  const toggleExpand = (key) => {
    setExpanded((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  /*
   * Items visible to current user
   */
  const visibleItems = items.filter((item) => {
    if (hasModuleAccess(userRole, item.to)) return true;
    if (item?.to?.startsWith("/rentals") && shouldInsertRentals) return true;
    if (item?.to?.startsWith("/novatra/marketplace")) return marketplaceEnabled;
    return false;
  });

  /*
   * Mobile tile
   */
  const MobileCardTile = ({ item }) => {
    const active = isRouteActive(item.to);

    return (
      <Button
        as={Link}
        to={item.to}
        h="auto"
        p={0}
        variant="unstyled"
        display="flex"
        flexDirection="column"
        gap={2}
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        _hover={{
          transform: "translateY(-4px)",
          transition: "transform 0.2s ease",
        }}
      >
        <Box
          w="80px"
          h="80px"
          borderRadius="lg"
          bg={active ? activeBg : mobileTileBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 0.2s ease"
          border="2px solid"
          borderColor={active ? activeColor : "transparent"}
        >
          <Icon
            as={item.icon}
            boxSize={10}
            color={active ? activeColor : textColor}
          />
        </Box>

        <Text
          fontSize="sm"
          fontWeight="medium"
          textAlign="center"
          color={textColor}
          maxW="92px"
          noOfLines={2}
        >
          {item.label}
        </Text>
      </Button>
    );
  };

  /*
   * Desktop parent navigation section
   */
  const SidebarSection = ({ item }) => {
    const active = isRouteActive(item.to);

    const isOpen = !!expanded[item.to];

    return (
      <Box key={item.to}>
        <Tooltip
          label={item.label}
          placement="right"
          isDisabled={!collapsed}
          openDelay={200}
          hasArrow
        >
          <Button
            width="100%"
            justifyContent={collapsed ? "center" : "flex-start"}
            leftIcon={<Icon as={item.icon} boxSize="1.05em" />}
            variant="ghost"
            fontWeight="medium"
            bg={active ? activeBg : "transparent"}
            color={active ? activeColor : textColor}
            _hover={{
              bg: hoverBg,
            }}
            size="sm"
            borderRadius="md"
            aria-expanded={isOpen}
            aria-controls={`${item.to}-children`}
            onClick={() => toggleExpand(item.to)}
            px={collapsed ? 0 : 2}
          >
            {!collapsed && (
              <Flex align="center" justify="space-between" width="100%">
                <Box>{item.label}</Box>

                <Icon as={isOpen ? BsChevronUp : BsChevronDown} />
              </Flex>
            )}
          </Button>
        </Tooltip>

        <Collapse in={isOpen && !collapsed} animateOpacity>
          <VStack
            id={`${item.to}-children`}
            align="stretch"
            spacing={1}
            ml={4}
            mt={1}
          >
            {item.children.map((child) => (
              <Button
                key={child.to}
                as={Link}
                to={child.to}
                justifyContent="flex-start"
                variant="ghost"
                size="sm"
                fontSize="sm"
                color={isRouteActive(child.to) ? activeColor : textColor}
                bg={isRouteActive(child.to) ? activeBg : "transparent"}
                _hover={{
                  bg: hoverBg,
                }}
              >
                {child.label}
              </Button>
            ))}
          </VStack>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      {/* MOBILE */}

      {shouldShowMobileSidebar && (
        <Box
          position="fixed"
          top={16}
          left={0}
          right={0}
          bottom={0}
          bg={bg}
          display={{
            base: "flex",
            md: "none",
          }}
          flexDirection="column"
          zIndex={20}
          overflowY="auto"
          py={6}
          px={4}
        >
          <VStack spacing={6} align="stretch">
            {/* Mobile Header */}

            <Flex justify="space-between" align="center">
              <Box>
                <Heading size="lg" mb={1}>
                  Navigation
                </Heading>

                <Text fontSize="sm" color={footerColor}>
                  Access all modules
                </Text>
              </Box>

              <IconButton
                icon={<BsArrowLeft />}
                onClick={() => {
                  setSidebarClosed(true);

                  navigate(-1);
                }}
                variant="ghost"
                size="sm"
                aria-label="Go back"
                title="Go back"
                _hover={{
                  bg: hoverBg,
                }}
              />
            </Flex>

            {/* Mobile modules */}

            <Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                textTransform="uppercase"
                mb={4}
                px={1}
                letterSpacing="0.04em"
                color={footerColor}
              >
                Novatra
              </Text>

              <SimpleGrid columns={3} spacing={4}>
                {visibleItems.map((item) => (
                  <MobileCardTile key={item.to} item={item} />
                ))}
              </SimpleGrid>
            </Box>

            <Box h={4} />
          </VStack>
        </Box>
      )}

      {/* DESKTOP */}

      <Flex
        position="fixed"
        top={16}
        left={0}
        bottom={0}
        w={collapsed ? "64px" : "240px"}
        bg={bg}
        borderRight="1px solid"
        borderColor={borderClr}
        h="calc(100dvh - 64px)"
        boxShadow="sm"
        transition="width 0.2s ease"
        direction="column"
        display={{
          base: "none",
          md: "flex",
        }}
      >
        {/* Collapse control */}

        <IconButton
          aria-label="Toggle sidebar"
          icon={collapsed ? <BsChevronRight /> : <BsChevronLeft />}
          size="sm"
          variant="solid"
          colorScheme="gray"
          position="absolute"
          top="50%"
          right={collapsed ? "-12px" : "-14px"}
          transform="translateY(-50%)"
          zIndex={2}
          borderRadius="full"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          boxShadow="md"
        />

        {/* Desktop content */}

        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          px={collapsed ? 4 : 5}
          py={3}
        >
          <VStack spacing={2} align="stretch">
            <Box
              display="flex"
              alignItems="center"
              justifyContent={collapsed ? "center" : "space-between"}
              mb={2}
            >
              <Text>Logo</Text>
            </Box>

            {visibleItems.map((item) =>
              item.children ? (
                <SidebarSection key={item.to} item={item} />
              ) : (
                <Box key={item.to}>
                  <Tooltip
                    label={item.label}
                    placement="right"
                    isDisabled={!collapsed}
                    openDelay={200}
                    hasArrow
                  >
                    <Button
                      as={Link}
                      to={item.to}
                      width="100%"
                      justifyContent={collapsed ? "center" : "flex-start"}
                      leftIcon={<Icon as={item.icon} boxSize="1.05em" />}
                      variant="ghost"
                      fontWeight="medium"
                      bg={isRouteActive(item.to) ? activeBg : "transparent"}
                      color={isRouteActive(item.to) ? activeColor : textColor}
                      _hover={{
                        bg: hoverBg,
                      }}
                      size="sm"
                      borderRadius="md"
                      aria-current={isRouteActive(item.to) ? "page" : undefined}
                      aria-label={item.label}
                      px={collapsed ? 0 : 2}
                    >
                      {!collapsed && item.label}
                    </Button>
                  </Tooltip>
                </Box>
              ),
            )}
          </VStack>

          {!collapsed && (
            <>
              <Divider my={6} borderColor={borderClr} />

              <Text px={1} fontSize="xs" color={footerColor}>
                © {new Date().getFullYear()} Novatra
              </Text>
            </>
          )}
        </Box>
      </Flex>
    </>
  );
};

export default Sidebar;
